import { useEffect, useState } from 'react';
import useProfileData from './use-profile-data';

type UserAccessPermissions = {
  READ?: string;
  MODIFY?: Array<string>;
  CREATE?: Array<string>;
};

interface UserAccessProps {
  initialAccess?: boolean;
  permissions?: UserAccessPermissions;
  mustMatchAll?: boolean;
}

export const useUserAccess = ({ initialAccess = false, permissions, mustMatchAll = false }: UserAccessProps) => {
  const [readOnly, setReadOnly] = useState(!initialAccess);
  const [editAccess, setEditAccess] = useState(initialAccess);
  const [createAccess, setCreateAccess] = useState(initialAccess);
  const [superUser, setSuperUser] = useState(false);
  const [isExternalUser, setIsExternalUser] = useState(false);

  const { profileData: { user: userData } = {} } = useProfileData();

  const setSuperUserPermissions = (isSuperUser: boolean) => {
    setReadOnly(!isSuperUser);
    setEditAccess(isSuperUser);
    setCreateAccess(isSuperUser);
    setSuperUser(isSuperUser);
  };

  useEffect(() => {
    if (userData && permissions) {
      const { adminAccess, superUser, isExternal } = userData;
      setIsExternalUser(Boolean(isExternal));

      if (superUser) {
        setSuperUserPermissions(true);
        return;
      }

      let canCreate = false;
      let canModify = false;
      let canView = false;
      if (adminAccess) {
        if (mustMatchAll) {
          canCreate = permissions.CREATE?.every((p) => adminAccess.includes(p)) ?? false;
          canModify = permissions.MODIFY?.every((p) => adminAccess.includes(p)) ?? false;
          canView = permissions.READ ? adminAccess.includes(permissions.READ) : false;
        } else {
          canCreate = permissions.CREATE?.some((p) => adminAccess.includes(p)) ?? false;
          canModify = permissions.MODIFY?.some((p) => adminAccess.includes(p)) ?? false;
          canView = permissions.READ ? adminAccess.includes(permissions.READ) : false;
        }
      }

      setReadOnly(Boolean(!canModify && canView));
      setEditAccess(canModify);
      setCreateAccess(canCreate);
      setSuperUser(false);
    }
  }, [userData, permissions, mustMatchAll]);

  return { readOnly, editAccess, createAccess, setReadOnly, setEditAccess, setCreateAccess, superUser, isExternalUser };
};
